import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Stack,
  Alert,
  Snackbar,
} from "@mui/material";
import Header from "../../../components/Header";
import API from "../../../api";

const COLORS = {
  bg: "#0a0b0d",
  surface: "#111318",
  border: "#1e2330",
  borderHover: "#2f3a52",
  accent: "#459095",
  accentDim: "rgba(69,144,149,0.08)",
  accentGlow: "rgba(69,144,149,0.25)",
  text: "#e8ecf4",
  muted: "#4a5568",
  error: "#ff4d6d",
  success: "#00e5a0",
};

const monoStack = "'Roboto Mono', 'Courier New', monospace";
const sansStack = "'DM Sans', sans-serif";

const GOOGLE_FONTS =
  "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Roboto+Mono:wght@400;500;700&display=swap";

const typeOptions = [
  { value: "full",      label: "Full",        desc: "Complete installation bundle" },
  { value: "full-auto", label: "Full · Auto", desc: "Silent / unattended install" },
  { value: "lite",      label: "Lite",         desc: "Lightweight core agent only" },
  { value: "gpo",       label: "GPO",          desc: "Group Policy deployment package" },
];

/* ── per-type explanation content ── */
const typeInfo = {
  full: {
    icon: "📊",
    title: "Full",
    badge: "Complete dashboard",
    badgeColor: "#459095",
    summary:
      "Full-featured agent dashboard with advanced graphics and extended tools for data supervision and analysis.",
    sections: [
      {
        heading: "What's included",
        items: [
          "Advanced dashboard with rich graphics",
          "Extended data supervision & analysis modules",
          "All available features unlocked",
          "Guided installation wizard",
        ],
      },
      {
        heading: "Activation",
        items: [
          "Activation code must be entered manually during setup",
          "Select the license key from the menu on the left",
        ],
      },
    ],
    requirements: ["Windows 10", "Windows 11", "Windows Server 2019 or later"],
    note: "You will be prompted to enter the activation code during installation. Make sure to have it ready.",
  },
  "full-auto": {
    icon: "📊",
    title: "Full (Auto Install)",
    badge: "Auto activation",
    badgeColor: "#a78bfa",
    summary:
      "Full-featured agent dashboard with advanced graphics and extended tools for data supervision and analysis.",
    sections: [
      {
        heading: "What's included",
        items: [
          "Advanced dashboard with rich graphics",
          "Extended data supervision & analysis modules",
          "All available features unlocked",
          ".lic file pre-bundled inside the zip",
        ],
      },
      {
        heading: "Activation",
        items: [
          "No manual activation code required during setup",
          "The .lic file inside the zip handles activation automatically",
          "Ideal for quick or remote deployments",
        ],
      },
    ],
    requirements: ["Windows 10", "Windows 11", "Windows Server 2019 or later"],
    note: "The .lic file is already included in the downloaded archive. No manual input is needed during installation.",
  },
  lite: {
    icon: "🪶",
    title: "Lite",
    badge: "Essential dashboard",
    badgeColor: "#34d399",
    summary:
      "Simplified and more restricted dashboard for data supervision. The agent core is identical to the Full version.",
    sections: [
      {
        heading: "What's included",
        items: [
          "Full agent core (same as Full version)",
          "Simplified dashboard",
          "Basic supervision features",
          ".lic file pre-bundled inside the zip",
        ],
      },
      {
        heading: "Activation",
        items: [
          "No manual activation code required during setup",
          "The .lic file inside the zip handles activation automatically",
        ],
      },
    ],
    requirements: ["Windows 10", "Windows 11", "Windows Server 2019 or later"],
    note: "Advanced analytics and graphics features are not included. The agent engine remains complete and identical to the Full version.",
  },
  gpo: {
    icon: "🏢",
    title: "GPO",
    badge: "Essential dashboard",
    badgeColor: "#fbbf24",
    summary:
      "Simplified and more restricted dashboard for data supervision, optimized for Group Policy deployment. The agent core is identical to the Full version.",
    sections: [
      {
        heading: "What's included",
        items: [
          "Full agent core (same as Full version)",
          "Simplified dashboard",
          "Basic supervision features",
          ".lic file pre-bundled inside the zip",
        ],
      },
      {
        heading: "Activation",
        items: [
          "No manual activation code required during setup",
          "The .lic file inside the zip handles activation automatically",
          "Package ready for Group Policy (AD) distribution",
        ],
      },
    ],
    requirements: ["Windows 10", "Windows 11", "Windows Server 2019 or later"],
    note: "Designed for enterprise environments with Active Directory. Deployment is centrally managed with no end-user interaction required.",
  },
};

/* ── helpers ── */

const Label = ({ children }) => (
  <Typography
    component="label"
    sx={{
      fontFamily: monoStack,
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: COLORS.accent,
      mb: 0.8,
      display: "block",
    }}
  >
    {children}
  </Typography>
);

const FieldError = ({ show, msg }) =>
  show ? (
    <Typography sx={{ fontFamily: monoStack, fontSize: "0.7rem", color: COLORS.error, mt: 0.6 }}>
      ⚠ {msg}
    </Typography>
  ) : null;

const selectSx = (hasError) => ({
  fontFamily: monoStack,
  fontSize: "0.88rem",
  color: COLORS.text,
  background: COLORS.surface,
  borderRadius: "6px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? COLORS.error : COLORS.border,
    transition: "border-color .2s",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? COLORS.error : COLORS.borderHover,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.accent,
    boxShadow: `0 0 0 3px ${COLORS.accentGlow}`,
  },
  "& .MuiSvgIcon-root": { color: COLORS.muted },
});

const menuProps = {
  PaperProps: {
    sx: {
      background: "#13161f",
      border: `1px solid ${COLORS.border}`,
      borderRadius: "8px",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontFamily: monoStack,
        fontSize: "0.85rem",
        color: COLORS.text,
        py: 1.2,
        "&:hover": { background: COLORS.accentDim },
        "&.Mui-selected": {
          background: COLORS.accentDim,
          color: COLORS.accent,
          "&:hover": { background: COLORS.accentDim },
        },
      },
    },
  },
};

/* ── download button ── */
const DownloadButton = ({ loading, onClick }) => (
  <Button
    onClick={onClick}
    disabled={loading}
    disableRipple
    sx={{
      alignSelf: "stretch",
      height: 52,
      fontFamily: monoStack,
      fontWeight: 700,
      fontSize: "0.85rem",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      borderRadius: "8px",
      border: `1.5px solid ${loading ? COLORS.border : COLORS.accent}`,
      color: loading ? COLORS.muted : COLORS.accent,
      background: loading ? "transparent" : COLORS.accentDim,
      position: "relative",
      overflow: "hidden",
      transition: "all .25s",
      "&:hover:not(:disabled)": {
        background: COLORS.accent,
        color: "#fff",
        boxShadow: `0 0 28px ${COLORS.accentGlow}`,
      },
      "&::before": loading
        ? {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: "30%",
            background: `linear-gradient(90deg, transparent, ${COLORS.accentDim}, transparent)`,
            animation: "shimmer 1.4s infinite",
          }
        : {},
      "@keyframes shimmer": {
        "0%": { left: "-30%" },
        "100%": { left: "110%" },
      },
    }}
  >
    {loading ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 14,
            height: 14,
            border: `2px solid ${COLORS.muted}`,
            borderTop: `2px solid ${COLORS.accent}`,
            borderRadius: "50%",
            animation: "spin .8s linear infinite",
            "@keyframes spin": { to: { transform: "rotate(360deg)" } },
          }}
        />
        Downloading…
      </Box>
    ) : (
      "Download"
    )}
  </Button>
);

/* ── right-side info panel ── */
const InfoPanel = ({ downloadType }) => {
  const info = typeInfo[downloadType];

  if (!info) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 240,
          border: `1px dashed ${COLORS.border}`,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          p: 4,
          color: COLORS.muted,
          textAlign: "center",
          minHeight: 220,
        }}
      >
        <Typography sx={{ fontSize: "2rem", lineHeight: 1 }}>🗂️</Typography>
        <Typography sx={{ fontFamily: monoStack, fontSize: "0.7rem", letterSpacing: "0.1em" }}>
          SELECT A TYPE
        </Typography>
        <Typography sx={{ fontFamily: sansStack, fontSize: "0.78rem", color: COLORS.muted, maxWidth: 180 }}>
          Details and guidance will appear here once you pick a download type.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      key={downloadType}
      sx={{
        flex: 1,
        minWidth: 240,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        animation: "fadeSlideIn .22s ease",
        "@keyframes fadeSlideIn": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* top accent bar matches badge color */}
      <Box sx={{ height: 3, background: `linear-gradient(90deg, ${info.badgeColor}, transparent)` }} />

      <Box sx={{ p: "22px 24px 24px" }}>
        {/* header row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
          <Typography sx={{ fontSize: "1.6rem", lineHeight: 1, mt: "2px" }}>{info.icon}</Typography>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <Typography
                sx={{ fontFamily: monoStack, fontWeight: 700, fontSize: "0.92rem", color: COLORS.text }}
              >
                {info.title}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: "2px",
                  borderRadius: "4px",
                  background: `${info.badgeColor}20`,
                  border: `1px solid ${info.badgeColor}50`,
                  fontFamily: monoStack,
                  fontSize: "0.58rem",
                  letterSpacing: "0.1em",
                  color: info.badgeColor,
                  textTransform: "uppercase",
                }}
              >
                {info.badge}
              </Box>
            </Box>
            <Typography sx={{ fontFamily: sansStack, fontSize: "0.78rem", color: COLORS.muted, lineHeight: 1.5 }}>
              {info.summary}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: 1, background: COLORS.border, mb: 2 }} />

        {/* sections */}
        <Stack spacing={2.5}>
          {info.sections.map((sec) => (
            <Box key={sec.heading}>
              <Typography
                sx={{
                  fontFamily: monoStack,
                  fontSize: "0.62rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: COLORS.accent,
                  mb: 1,
                }}
              >
                {sec.heading}
              </Typography>
              <Stack spacing={0.6}>
                {sec.items.map((item) => (
                  <Box key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        background: info.badgeColor,
                        mt: "6px",
                        flexShrink: 0,
                        opacity: 0.8,
                      }}
                    />
                    <Typography sx={{ fontFamily: sansStack, fontSize: "0.8rem", color: COLORS.text, lineHeight: 1.5 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* system requirements */}
        <Box sx={{ mt: 2.5 }}>
          <Typography
            sx={{
              fontFamily: monoStack,
              fontSize: "0.62rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: COLORS.accent,
              mb: 1,
            }}
          >
            System Requirements
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
            {info.requirements.map((os) => (
              <Box
                key={os}
                sx={{
                  px: 1.2,
                  py: "4px",
                  borderRadius: "5px",
                  border: `1px solid ${COLORS.border}`,
                  background: "rgba(255,255,255,0.03)",
                  fontFamily: monoStack,
                  fontSize: "0.7rem",
                  color: COLORS.text,
                  whiteSpace: "nowrap",
                }}
              >
                🪟 {os}
              </Box>
            ))}
          </Box>
        </Box>

        {/* note box */}
        <Box
          sx={{
            mt: 2,
            p: "10px 12px",
            borderRadius: "6px",
            background: "rgba(255,255,255,0.025)",
            border: `1px solid ${COLORS.border}`,
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <Typography sx={{ fontSize: "0.75rem", lineHeight: 1, mt: "1px", flexShrink: 0 }}>ℹ️</Typography>
          <Typography sx={{ fontFamily: sansStack, fontSize: "0.75rem", color: COLORS.muted, lineHeight: 1.5 }}>
            {info.note}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
const DownloadPackagePage = () => {
  const [downloadType, setDownloadType] = useState("");
  const [license, setLicense]           = useState("");
  const [licenses, setLicenses]         = useState([]);
  const [submitted, setSubmitted]       = useState(false);
  const [downloading, setDownloading]   = useState(false);
  const [errorMsg, setErrorMsg]         = useState("");
  const [successMsg, setSuccessMsg]     = useState("");

  useEffect(() => {
    if (!document.getElementById("gf-download-pkg")) {
      const link = document.createElement("link");
      link.id   = "gf-download-pkg";
      link.rel  = "stylesheet";
      link.href = GOOGLE_FONTS;
      document.head.appendChild(link);
    }
    API.getLicenseCodesAgent()
      .then((res) => setLicenses(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setLicenses([]);
        setErrorMsg("Errore nel caricamento delle licenze.");
      });
  }, []);

  const handleDownload = async () => {
    setSubmitted(true);
    setErrorMsg("");
    setSuccessMsg("");
    if (!downloadType || !license) return;

    setDownloading(true);
    try {
      const res = await API.downloadMsiAgent({ codiceAttivazione: license, type: downloadType });
      let fileName = "agent-package.zip";
      const cd = res.headers?.["content-disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/i);
        if (m?.[1]) fileName = m[1];
      }
      const url  = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href  = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMsg("Download completato con successo!");
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          setErrorMsg(json?.error || "Errore durante il download.");
        } catch { setErrorMsg("Errore durante il download."); }
      } else {
        setErrorMsg(error.response?.data?.error || "Errore durante il download.");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box m="20px" sx={{ fontFamily: sansStack, "& *": { boxSizing: "border-box" } }}>
      <Header title="DOWNLOAD PACKAGE" subtitle="Download installer files" />

      <Box
        sx={{
          mt: 4,
          mb: 3,
          height: 1,
          background: `linear-gradient(90deg, ${COLORS.accent} 0%, ${COLORS.border} 60%, transparent 100%)`,
          opacity: 0.6,
        }}
      />

      {/* two-column layout */}
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* ── LEFT: form ── */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", md: 460 },
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "12px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box sx={{ height: 3, background: `linear-gradient(90deg, ${COLORS.accent}, transparent)` }} />

          <Box
            sx={{
              position: "absolute", top: 12, right: 16,
              fontFamily: monoStack, fontSize: "0.6rem",
              letterSpacing: "0.14em", color: COLORS.muted, textTransform: "uppercase",
            }}
          >
            agent installer
          </Box>

          <Stack spacing={3} sx={{ p: "28px 32px 32px" }}>
            <Box>
              <Typography sx={{ fontFamily: monoStack, fontWeight: 700, fontSize: "1.15rem", color: COLORS.text }}>
                Agent&nbsp;<Box component="span" sx={{ color: COLORS.accent }}>Download</Box>
              </Typography>
              <Typography sx={{ fontFamily: sansStack, fontSize: "0.82rem", color: COLORS.muted, mt: 0.4 }}>
                Select package type and activation license to proceed.
              </Typography>
            </Box>

            {errorMsg && (
              <Box
                sx={{
                  border: `1px solid ${COLORS.error}`,
                  borderLeft: `3px solid ${COLORS.error}`,
                  borderRadius: "6px",
                  p: "10px 14px",
                  background: "rgba(255,77,109,0.07)",
                  fontFamily: monoStack,
                  fontSize: "0.78rem",
                  color: COLORS.error,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {errorMsg}
                <Box
                  component="button"
                  onClick={() => setErrorMsg("")}
                  sx={{
                    background: "none", border: "none", color: COLORS.muted,
                    cursor: "pointer", fontSize: "1rem", lineHeight: 1, p: 0,
                    "&:hover": { color: COLORS.text },
                  }}
                >×</Box>
              </Box>
            )}

            <Box sx={{ height: 1, background: COLORS.border }} />

            <Box>
              <Label>01 · Download Type</Label>
              <FormControl fullWidth>
                <Select
                  value={downloadType}
                  onChange={(e) => setDownloadType(e.target.value)}
                  displayEmpty
                  sx={selectSx(submitted && !downloadType)}
                  MenuProps={menuProps}
                  renderValue={(val) => {
                    const opt = typeOptions.find((o) => o.value === val);
                    return opt
                      ? <span>{opt.label}</span>
                      : <span style={{ color: COLORS.muted }}>— select type —</span>;
                  }}
                >
                  {typeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box>
                        <span style={{ color: COLORS.accent, marginRight: 10 }}>{opt.label}</span>
                        <span style={{ color: COLORS.muted, fontSize: "0.78rem" }}>{opt.desc}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FieldError show={submitted && !downloadType} msg="Please select a download type" />
            </Box>

            <Box>
              <Label>02 · Activation License</Label>
              <FormControl fullWidth>
                <Select
                  value={license}
                  onChange={(e) => setLicense(e.target.value)}
                  displayEmpty
                  sx={selectSx(submitted && !license)}
                  MenuProps={menuProps}
                  renderValue={(val) =>
                    val || <span style={{ color: COLORS.muted }}>— select license —</span>
                  }
                >
                  {licenses.map((item, i) => (
                    <MenuItem key={i} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FieldError show={submitted && !license} msg="Please select a license" />
            </Box>

            <Box sx={{ height: 1, background: COLORS.border }} />

            <DownloadButton loading={downloading} onClick={handleDownload} />
          </Stack>
        </Box>

        {/* ── RIGHT: info panel ── */}
        <InfoPanel downloadType={downloadType} />
      </Box>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessMsg("")}
          sx={{
            background: "#0a1a14",
            border: `1px solid ${COLORS.success}`,
            color: COLORS.success,
            fontFamily: monoStack,
            fontSize: "0.8rem",
            "& .MuiAlert-icon": { color: COLORS.success },
          }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DownloadPackagePage;